import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { CURRENCIES, REVALIDATE_MODES } from '@savgroup-front-common/constants';
import { ErrorFromBack } from '@savgroup-front-common/core/helpers';
import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import { ClaimService } from '@/api';
import { SolutionSummaryDto } from '@/types/SolutionSummaryDto';
import { useGetClaimGroupConfirmation } from '@/view/app/hooks/useGetClaimGroupConfirmation';

import useGetClaimGroupSummary, {
  USE_GET_CLAIMS_GROUP_SUMMARY,
} from '../hooks/useGetClaimGroupSummary';
import { useGetSolutionsByClaim } from '../hooks/useGetSolutionsByClaim';
import { IrshStepValues } from '../IrshPages.types';

import ClaimGroupSolutionSchema from './NewClaimGroupSolutionPage.schema';
import { ClaimGroupSolutionValues } from './NewClaimGroupSolutionPage.types';

interface Args {
  onNextStep: RoutedStepProps<IrshStepValues>['onNextStep'];
}

const useClaimGroupSolutionPage = ({ onNextStep }: Args) => {
  const { pushErrors } = useToasts();
  const queryClient = useQueryClient();
  const { values, onValuesUpdate } =
    useRoutedStepsOrchestratorContext<IrshStepValues>();

  const claimGroupId = values?.claimGroupId;

  const { claims } = useGetClaimGroupSummary({
    claimGroupId,
    suspense: true,
  });

  const { mutateAsync: handleGetHandling, isLoading: isLoadingGetHandling } =
    useMutation(
      ['getHandling'],
      async ({ claimGroupId }: { claimGroupId: string }) => {
        if (!claimGroupId) {
          return undefined;
        }

        const responseHandling = await ClaimService.getHandlingByClaimGroup({
          claimGroupId,
        });

        if (!responseHandling || responseHandling.failure) {
          pushErrors(responseHandling?.errors as ErrorFromBack[]);

          return undefined;
        }

        return responseHandling.value;
      },
    );

  const handleSubmit = useCallback(
    async (solutionValue: SolutionSummaryDto) => {
      if (!claims) {
        throw new Error('Claims are not defined');
      }
      if (!claimGroupId) {
        throw new Error('ClaimGroupId is not defined');
      }
      if (!solutionValue) {
        return undefined;
      }

      try {
        const response = await ClaimService.setClaimGroupSolutionCommand({
          claimGroupId,
          solutionTypeId: solutionValue?.solutionTypeId,
          solutionPrice: solutionValue?.price
            ? {
                amount: solutionValue.price,
                currency: solutionValue?.priceCurrencyCode as CURRENCIES,
              }
            : undefined,
        });

        if (response.failure) {
          pushErrors(response.errors);

          return undefined;
        }

        const responseHandling = await handleGetHandling({
          claimGroupId,
        });

        if (!responseHandling) {
          return undefined;
        }

        await queryClient.invalidateQueries([
          USE_GET_CLAIMS_GROUP_SUMMARY,
          { claimGroupId },
        ]);

        onNextStep({
          newValues: {
            solution: solutionValue,
            handling: responseHandling,
            handlingDeposit:
              values?.solution !== solutionValue
                ? undefined
                : values?.handlingDeposit,
            handlingDelivery:
              values?.solution !== solutionValue
                ? undefined
                : values?.handlingDelivery,
          },
        });

        return undefined;
      } catch (err: any) {
        pushErrors(err);

        return undefined;
      }
    },
    [
      claimGroupId,
      claims,
      handleGetHandling,
      onNextStep,
      pushErrors,
      queryClient,
      values?.solution,
      values?.handlingDeposit,
      values?.handlingDelivery,
    ],
  );

  const { mutateAsync: handleAutoSetSolution, isLoading: isSkippingSolution } =
    useMutation(
      async ({ solution }: { solution: SolutionSummaryDto }) => {
        if (!claims) {
          throw new Error('Claims are not defined');
        }
        if (!claimGroupId) {
          throw new Error('ClaimGroupId is not defined');
        }

        try {
          const response = await ClaimService.setClaimGroupSolutionCommand({
            claimGroupId,
            solutionTypeId: solution?.solutionTypeId,
            solutionPrice: solution?.price
              ? {
                  amount: solution.price,
                  currency: solution?.priceCurrencyCode as CURRENCIES,
                }
              : undefined,
          });

          if (response.failure) {
            logError(response.errors);

            return undefined;
          }

          const responseHandling = await handleGetHandling({
            claimGroupId,
          });

          if (!responseHandling) {
            return undefined;
          }

          await queryClient.invalidateQueries([
            USE_GET_CLAIMS_GROUP_SUMMARY,
            { claimGroupId },
          ]);

          onNextStep({
            newValues: {
              solution: solution,
              handling: responseHandling,
            },
          });
        } catch (err: any) {
          pushErrors(err);

          return undefined;
        }

        return undefined;
      },
      {
        onError: () => {
          onValuesUpdate({
            newValues: {
              solution: {
                solutionTypeId: '',
              } as any,
            },
          });
        },
      },
    );

  const { solutions, isSolutionsLoading } = useGetSolutionsByClaim({
    claimGroupId,
    suspense: true,
  });

  const { claimGroupConfirmation } = useGetClaimGroupConfirmation({
    claimGroupId,
  });

  const formContext = useForm<ClaimGroupSolutionValues>({
    resolver: yupResolver(ClaimGroupSolutionSchema),
    defaultValues: {
      chooseSolution: claims?.at(0)?.solutionTypeId,
    },
    mode: REVALIDATE_MODES.ON_CHANGE,
  });

  const onSubmit = useCallback(
    async ({ chooseSolution }: ClaimGroupSolutionValues) => {
      if (!claims) {
        throw new Error('Claims are not defined');
      }

      const solutionValue = solutions?.find(
        (solution) => solution.solutionTypeId === chooseSolution,
      );

      if (solutionValue) {
        await handleSubmit(solutionValue);

        return undefined;
      }

      return undefined;
    },
    [claims, handleSubmit, solutions],
  );

  const firstSolution = solutions?.at(0);

  useEffect(() => {
    if (
      solutions?.length === 1 &&
      (values?.solution?.solutionTypeId === undefined ||
        values?.solution?.solutionTypeId !== firstSolution?.solutionTypeId) &&
      firstSolution &&
      !isSkippingSolution
    ) {
      onValuesUpdate({
        newValues: {
          solution: {
            solutionTypeId: '',
          } as any,
        },
      });

      handleAutoSetSolution({
        solution: firstSolution,
      });
    }
  }, [
    isSkippingSolution,
    solutions?.length,
    firstSolution,
    values?.solution?.solutionTypeId,
    onValuesUpdate,
    handleAutoSetSolution,
  ]);

  return {
    solutions,
    claimGroupConfirmation,
    formContext,
    onSubmit,
    isLoadingSubmit: isLoadingGetHandling,
    isSkippingSolution,
    isSolutionsLoading,
  };
};

export default useClaimGroupSolutionPage;
